import type { ApplicationSubmissionDocument, ProductionDocument } from '@/types'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  serverTimestamp,
  orderBy,
  where,
  query,
  onSnapshot,
} from '@/lib/firebase/firestore'

const col = () => typedCollection<ApplicationSubmissionDocument>('applicationSubmissions')
const doc = (id: string) => typedDoc<ApplicationSubmissionDocument>('applicationSubmissions', id)

export const adminSubmissionsService = {
  subscribeByProductionId(
    productionId: string,
    onNext: (
      submissions: Array<ApplicationSubmissionDocument & { id: string }>,
      current: boolean
    ) => void,
    onError: (error: unknown) => void
  ): () => void {
    let production: ProductionDocument | null = null
    let submissions: Array<ApplicationSubmissionDocument & { id: string }> = []
    let productionReady = false
    let submissionsReady = false
    let productionCurrent = false
    let submissionsCurrent = false
    const emit = () => {
      if (!productionReady || !submissionsReady) return
      onNext(
        submissions.filter(
          (s) =>
            (!s.applicationType || s.applicationType === 'production_intake') &&
            !s.intakeSubmissionId &&
            (!production?.intakeApplicationId || s.applicationId === production.intakeApplicationId)
        ),
        productionCurrent && submissionsCurrent
      )
    }
    const stopProduction = onSnapshot(
      typedDoc<ProductionDocument>('productions', productionId),
      (snapshot) => {
        production = snapshot.data() ?? null
        productionReady = true
        productionCurrent = !snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites
        emit()
      },
      onError,
      undefined,
      { includeMetadataChanges: true }
    )
    const stopSubmissions = onSnapshot(
      query(col(), where('productionId', '==', productionId)),
      (snapshot) => {
        submissions = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }))
        submissionsReady = true
        submissionsCurrent = !snapshot.metadata.fromCache && !snapshot.metadata.hasPendingWrites
        emit()
      },
      onError,
      undefined,
      { includeMetadataChanges: true }
    )
    return () => {
      stopProduction()
      stopSubmissions()
    }
  },
  async getAll(): Promise<Array<ApplicationSubmissionDocument & { id: string }>> {
    return getCollection(col(), orderBy('submittedAt', 'desc'))
  },

  async getCountsByApplicationId(): Promise<Record<string, number>> {
    const all = await getCollection(col())
    const counts: Record<string, number> = {}
    for (const s of all) {
      counts[s.applicationId] = (counts[s.applicationId] ?? 0) + 1
    }
    return counts
  },

  async getByApplicationId(
    applicationId: string
  ): Promise<Array<ApplicationSubmissionDocument & { id: string }>> {
    return getCollection(
      col(),
      where('applicationId', '==', applicationId),
      orderBy('submittedAt', 'desc')
    )
  },

  async updateStatus(id: string, status: ApplicationSubmissionDocument['status']): Promise<void> {
    await updateDocument(doc(id), {
      status,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ApplicationSubmissionDocument>)
  },

  async updateRank(id: string, rank: number): Promise<void> {
    await updateDocument(doc(id), {
      rank,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ApplicationSubmissionDocument>)
  },

  async updateCuratorNotes(id: string, curatorNotes: string): Promise<void> {
    await updateDocument(doc(id), {
      curatorNotes,
      updatedAt: serverTimestamp(),
    } as unknown as Partial<ApplicationSubmissionDocument>)
  },

  /**
   * Atomic curation move: update status, optional rank, and clear ranks that
   * no longer apply when leaving the Accepted column.
   */
  async curationMove(
    id: string,
    nextStatus: ApplicationSubmissionDocument['status'],
    nextRank: number | null
  ): Promise<void> {
    const patch: Record<string, unknown> = {
      status: nextStatus,
      updatedAt: serverTimestamp(),
    }
    if (nextRank !== null) patch.rank = nextRank
    await updateDocument(doc(id), patch as Partial<ApplicationSubmissionDocument>)
  },

  async getByProductionId(
    productionId: string
  ): Promise<Array<ApplicationSubmissionDocument & { id: string }>> {
    const production = await getDocument(typedDoc<ProductionDocument>('productions', productionId))
    const submissions = await getCollection(col(), where('productionId', '==', productionId))
    return submissions.filter(
      (s) =>
        (!s.applicationType || s.applicationType === 'production_intake') &&
        !s.intakeSubmissionId &&
        (production?.intakeApplicationId
          ? s.applicationId === production.intakeApplicationId
          : !s.applicationType || s.applicationType === 'production_intake')
    )
  },

  async getById(id: string): Promise<(ApplicationSubmissionDocument & { id: string }) | null> {
    const data = await getDocument(doc(id))
    return data ? { ...data, id } : null
  },

  async delete(id: string): Promise<void> {
    await deleteDocument(doc(id))
  },
}
